<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import {
  computed,
  reactive,
  ref,
  toRef,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';

import type { VRouterTab } from '@components/tabs/VRouterTabs/types';
import type { ITableColumnsOptions, ITableState } from '@modules/tables/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VRouterTabs from '@components/tabs/VRouterTabs/VRouterTabs.vue';
import { ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME } from '@modules/affiliates-approved/constants';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import PostbackChangeActivitySwitch from '@modules/postbacks/components/PostbackChangeActivitySwitch/PostbackChangeActivitySwitch.vue';
import PostbacksAddDialog from '@modules/postbacks/components/PostbacksAddDialog/PostbacksAddDialog.vue';
import { usePostbackTypes } from '@modules/postbacks/composables';
import {
  ADMIN_POSTBACKS_LIST_ROUTE_NAME,
  AFFILIATE_POSTBACKS_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_POSTBACKS,
} from '@modules/postbacks/constants';
import {
  usePostbacksAffiliateGetQuery,
  usePostbacksListQuery,
} from '@modules/postbacks/queries';
import {
  ADMIN_POSTBACKS_SETTINGS_ROUTE_NAME,
  AFFILIATE_POSTBACKS_SETTINGS_ROUTE_NAME,
} from '@modules/postbacks/submodules/edit/constants';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import { useUserStore } from '@modules/user/store';
import { isString } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

const isDisabled = ref(false);
const { getTPath } = useI18n();
const { t } = useTranslation();
const route = useRoute();
const { isAdminNamespace, isAffiliateNamespace } = useCurrentNamespace();
const isSupervisionSignOutProcess = toRef(useUserStore(), 'isSupervisionSignOutProcess');

const postbacksTablesState = reactive<Record<string, ITableState>>({
  registration: { page: 1 },
  deposit: { page: 1 },
  activity: { page: 1 },
});

const type = computed(() => (isString(route.params.type) ? route.params.type : ''));
const id = computed(() => (isString(route.params.id) ? Number(route.params.id) : undefined));

const editPage = computed(() => {
  if (isAdminNamespace.value)
    return ADMIN_POSTBACKS_SETTINGS_ROUTE_NAME;
  if (isAffiliateNamespace.value)
    return AFFILIATE_POSTBACKS_SETTINGS_ROUTE_NAME;
  return undefined;
});

const listPage = computed<symbol | undefined>(() => {
  if (isAdminNamespace.value)
    return ADMIN_POSTBACKS_LIST_ROUTE_NAME;
  if (isAffiliateNamespace.value)
    return AFFILIATE_POSTBACKS_LIST_ROUTE_NAME;
  return undefined;
});

const isEditButtonDisplayed = computed<boolean>(() => !!(type.value && editPage.value && !isSupervisionSignOutProcess.value));

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(usePostbacksListQuery, type, isDisabled, id);

const columnOptions: ITableColumnsOptions = {
  uri: {
    defaultMobilePreview: true,
  },
};

const { data: affiliate } = usePostbacksAffiliateGetQuery(id);

const { typeOptions } = usePostbackTypes();

const tableTabs = computed<VRouterTab[] | undefined>(() => (listPage.value && !isSupervisionSignOutProcess.value
  ? typeOptions.value.map((item) => ({
      label: item.label,
      route: {
        name: listPage.value,
        params: {
          id: id.value,
          type: item.value,
        },
      },
    }))
  : undefined));

function onTabClick(): void {
  isDisabled.value = true;
}

watchImmediate(tableState, (newState) => {
  postbacksTablesState[type.value] = newState;
});

watch(type, (newValue) => {
  if (newValue) {
    updateTableState(postbacksTablesState[newValue]);
    isDisabled.value = false;
  }
});

const title = computed(() => {
  if (isAdminNamespace.value) {
    const baseHeader = getTPath(TRANSLATION_GROUP_POSTBACKS, 'ADMIN_HEADER');

    if (affiliate.value) {
      return `${t(baseHeader)} ${id.value} - ${affiliate.value.email} - ${affiliate.value.username}`;
    }
  }

  return getTPath(TRANSLATION_GROUP_POSTBACKS, 'HEADER');
});
</script>

<template>
  <VCard v-data-qa-auto-id="'PostbacksListPage'" type="table">
    <VTable
      :data="data"
      :state="postbacksTablesState[type]"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="title"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #tabs>
        <VRouterTabs
          v-if="tableTabs"
          :items="tableTabs"
          replace
          @click="onTabClick"
        />
      </template>
      <template #recordAction="{ listItem }">
        <div class="flex items-center justify-center gap-2">
          <PostbackChangeActivitySwitch
            :item="listItem"
            :affiliate-id="id"
          />
          <VIconButton
            v-if="isEditButtonDisplayed"
            icon="pi-file-edit"
            :to="{
              name: editPage,
              params: {
                id,
                type,
                postbackId: String(listItem.id),
              },
            }"
          />
        </div>
      </template>
      <template
        #footerButtons
      >
        <PostbacksAddDialog />
        <VBackButton
          v-if="isAdminNamespace"
          :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'ADMIN_BACK')"
          :to="{
            name: ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME,
          }"
        />
      </template>
    </VTable>
  </VCard>
</template>
